// modules/chariot/ui/src/components/FeaturebaseWidget.tsx
import { useGateValue } from '@statsig/react-bindings';
import { useEffect } from 'react';

import { useAxios } from '@/hooks/useAxios';
import { useAuth } from '@/state/auth';

// Decode JWT payload to extract expiration time (without validation - server validates)
const getJwtExpiration = (token: string): number | null => {
  try {
    const payload = token.split('.')[1];
    const decoded = JSON.parse(atob(payload));
    return decoded.exp || null; // exp is in seconds since epoch
  } catch {
    return null;
  }
};

// Extend window type for Featurebase
interface FeaturebaseFunction {
  (action: string, config?: Record<string, unknown>): void;
  q?: Array<[string, Record<string, unknown> | undefined]>;
}

declare global {
  interface Window {
    Featurebase?: FeaturebaseFunction;
  }
}

// Global flag to prevent multiple injections across component remounts
let isFeaturebaseInjected = false;

// Global refresh timer ID to persist across component remounts
let refreshTimerId: number | null = null;

// Fetch JWT token from broker
const fetchFeaturebaseJwt = async (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  axios: { post: (url: string, data: unknown) => Promise<any> }
): Promise<string | undefined> => {
  try {
    const response = await axios.post('/broker', {
      credentialId: 'featurebase',
      type: 'featurebase',
      operation: 'get',
      format: ['token'],
    });

    const token = response.data?.credentialValue?.featurebase;

    if (!token) {
      console.warn('[Featurebase] JWT not found in broker response');
    }

    return token;
  } catch (error) {
    console.warn('[Featurebase] Failed to fetch JWT from broker:', error);
    return undefined;
  }
};

// Schedule token refresh before expiration
const scheduleTokenRefresh = (
  token: string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  axios: { post: (url: string, data: unknown) => Promise<any> }
) => {
  const exp = getJwtExpiration(token);
  if (!exp) {
    console.warn(
      '[Featurebase] Could not parse JWT expiration, skipping refresh scheduling'
    );
    return;
  }

  const nowInSeconds = Math.floor(Date.now() / 1000);
  const timeUntilExpiry = exp - nowInSeconds;

  const fiveMinutes = 5 * 60;

  // If token is already expired or about to expire, refresh immediately
  if (timeUntilExpiry <= fiveMinutes) {
    console.warn(
      '[Featurebase] Token expired or expiring soon, refreshing now'
    );
    void refreshFeaturebaseToken(axios);
    return;
  }

  const fiveMinBeforeExpiry = timeUntilExpiry - fiveMinutes;

  // Clear any existing timer
  if (refreshTimerId) {
    clearTimeout(refreshTimerId);
  }

  refreshTimerId = window.setTimeout(() => {
    void refreshFeaturebaseToken(axios);
  }, fiveMinBeforeExpiry * 1000);
};

// Refresh the JWT token and re-identify with Featurebase
const refreshFeaturebaseToken = async (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  axios: { post: (url: string, data: unknown) => Promise<any> }
) => {
  if (!window.Featurebase) {
    console.warn('[Featurebase] SDK not loaded, skipping token refresh');
    return;
  }

  const newToken = await fetchFeaturebaseJwt(axios);

  if (!newToken) {
    return;
  }

  // Re-identify with Featurebase using the new token
  window.Featurebase('reload', {
    featurebaseJwt: newToken,
  });

  // Schedule next refresh
  scheduleTokenRefresh(newToken, axios);
};

// Test utility to reset the injection flag (only for testing)
// eslint-disable-next-line react-refresh/only-export-components
export const __resetFeaturebaseForTesting = () => {
  isFeaturebaseInjected = false;
  if (refreshTimerId) {
    clearTimeout(refreshTimerId);
    refreshTimerId = null;
  }
  const script = document.getElementById('featurebase-sdk');
  if (script) {
    document.body.removeChild(script);
  }
  delete window.Featurebase;
};

export const FeaturebaseWidget = () => {
  const { isSignedIn, me, friend, sub } = useAuth();
  const axios = useAxios();

  // Use Statsig gate value, with environment variable as override for local dev ONLY
  const statsigGate = useGateValue('featurebase_widget_enabled');
  const envOverride =
    import.meta.env.DEV && import.meta.env.VITE_FEATUREBASE_ENABLED === 'true';

  // Show widget if Statsig gate is enabled OR (in dev mode AND env override is true)
  const showWidget = statsigGate || envOverride;

  useEffect(() => {
    // Don't inject if not signed in or gate not enabled
    if (!isSignedIn || !showWidget) {
      return;
    }

    // CRITICAL: Check both global flag AND DOM to prevent duplicate injection
    if (isFeaturebaseInjected || document.getElementById('featurebase-sdk')) {
      return;
    }

    const appId = import.meta.env.VITE_FEATUREBASE_APP_ID;
    if (!appId) {
      console.warn('[Featurebase] VITE_FEATUREBASE_APP_ID not configured');
      return;
    }

    // Use impersonated user if applicable, otherwise primary user
    const userEmail = me || friend;
    const userId = sub || userEmail;

    isFeaturebaseInjected = true;

    // Initialize Featurebase SDK loader function
    const featurebaseFunction: FeaturebaseFunction = function (action, config) {
      if (!featurebaseFunction.q) {
        featurebaseFunction.q = [];
      }
      featurebaseFunction.q.push([action, config]);
    };
    window.Featurebase = featurebaseFunction;

    // Inject Featurebase widget script
    const script = document.createElement('script');
    script.id = 'featurebase-sdk';
    script.src = 'https://do.featurebase.app/js/sdk.js';
    script.async = true;
    // TODO: Add SRI hash when available from Featurebase (R-002)
    // script.integrity = 'sha384-[HASH]';
    // script.crossOrigin = 'anonymous';

    script.onerror = error => {
      console.error('[Featurebase] Failed to load SDK:', error);
    };

    script.onload = async () => {
      if (!window.Featurebase) {
        console.error(
          '[Featurebase] SDK loaded but window.Featurebase not defined'
        );
        return;
      }

      // Fetch JWT from broker before booting Featurebase
      const jwtToken = await fetchFeaturebaseJwt(axios);

      try {
        // Boot Featurebase with user identification (required for security)
        window.Featurebase('boot', {
          appId: appId,
          email: userEmail, // User email for identification
          userId: userId, // User ID (using email as ID)
          featurebaseJwt: jwtToken, // JWT for authentication (optional, undefined if fetch failed)
          theme: 'light', // Match Chariot's theme
          language: 'en', // English language
        });

        // Schedule token refresh if we have a JWT
        if (jwtToken) {
          scheduleTokenRefresh(jwtToken, axios);
        }
      } catch (error) {
        console.error('[Featurebase] Failed to initialize:', error);
      }
    };

    document.body.appendChild(script);

    // Cleanup: DON'T remove script or reset flag to prevent re-injection
    // The widget should persist for the entire session
    return () => {
      // Clear refresh timer on unmount (will be rescheduled on remount if needed)
      if (refreshTimerId) {
        clearTimeout(refreshTimerId);
        refreshTimerId = null;
      }
      // Do NOT remove script or reset isFeaturebaseInjected flag
      // Widget should persist even if component remounts
    };

    // to prevent re-injection when user impersonates. Widget initializes once per session.
  }, [isSignedIn, showWidget]);

  return null; // Non-rendering component
};
